import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';
import { Session } from '../types';

const SessionDetailPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sessionId) {
      fetchSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const fetchSession = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/v1/sessions/${sessionId}`);
      setSession(response.data.data);
    } catch (err: any) {
      console.error('Error fetching session:', err);
      setError(err.response?.data?.error?.message || 'Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: { class: 'bg-secondary', icon: 'clock' },
      confirmed: { class: 'bg-success', icon: 'check-circle' },
      rejected: { class: 'bg-danger', icon: 'times-circle' }
    };
    return badges[status as keyof typeof badges] || badges.draft;
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error || 'Session not found'}
        </div>
        <Link to="/sessions" className="btn btn-secondary">
          <i className="fas fa-arrow-left me-2"></i>
          Back to Sessions
        </Link>
      </div>
    );
  }

  const statusBadge = getStatusBadge(session.status);

  return (
    <div className="container mt-4">
      <div className="mb-3">
        <Link to="/sessions" className="btn btn-link text-decoration-none">
          <i className="fas fa-arrow-left me-2"></i>
          Back to Sessions
        </Link>
      </div>

      <div className="card">
        <div className="card-header bg-white">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h3 className="mb-1">{session.talk_title || 'Untitled Talk'}</h3>
              <p className="text-muted mb-0">
                <i className="fas fa-calendar me-1"></i>
                {session.event_name || 'Unknown Event'}
              </p>
            </div>
            <span className={`badge ${statusBadge.class}`}>
              <i className={`fas fa-${statusBadge.icon} me-1`}></i>
              {session.status}
            </span>
          </div>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <strong className="text-muted">Talk:</strong>
              <div>
                <Link to={`/talks/${session.talk_id}`}>
                  {session.talk_title || 'View Talk'}
                </Link>
              </div>
            </div>
            <div className="col-md-6 mb-3">
              <strong className="text-muted">Event:</strong>
              <div>
                <Link to={`/events/${session.event_id}`}>
                  {session.event_name || 'View Event'}
                </Link>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <strong className="text-muted">Speaker:</strong>
              <div>{session.speaker_name || 'Unknown'}</div>
            </div>
            <div className="col-md-6 mb-3">
              <strong className="text-muted">Status:</strong>
              <div className="text-capitalize">{session.status}</div>
            </div>
          </div>

          {session.start_time && (
            <div className="row">
              <div className="col-md-6 mb-3">
                <strong className="text-muted">Start Time:</strong>
                <div>{new Date(session.start_time).toLocaleString()}</div>
              </div>
            </div>
          )}

          {session.room_or_track && (
            <div className="row">
              <div className="col-md-6 mb-3">
                <strong className="text-muted">Room/Track:</strong>
                <div>{session.room_or_track}</div>
              </div>
            </div>
          )}

          <div className="row">
            <div className="col-md-6 mb-3">
              <strong className="text-muted">Created:</strong>
              <div>{new Date(session.created_at).toLocaleString()}</div>
            </div>
            {session.updated_at !== session.created_at && (
              <div className="col-md-6 mb-3">
                <strong className="text-muted">Last Updated:</strong>
                <div>{new Date(session.updated_at).toLocaleString()}</div>
              </div>
            )}
          </div>

          {session.status === 'draft' && (
            <div className="alert alert-info mt-3">
              <i className="fas fa-info-circle me-2"></i>
              This session is pending approval from the event organizer.
            </div>
          )}

          {session.status === 'rejected' && (
            <div className="alert alert-warning mt-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              This session was not accepted by the event organizer.
            </div>
          )}

          {session.status === 'confirmed' && (
            <div className="alert alert-success mt-3">
              <i className="fas fa-check-circle me-2"></i>
              This session has been confirmed by the event organizer.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionDetailPage;
